import { getListStaff } from '../../../../services/global/admin.service';
import { useCallback, useEffect, useState } from 'react';

export type StaffStatus =
  | 'pending_invite'
  | 'invited_pending_signup'
  | 'registered_pending_onboarding'
  | 'registered_onboarded';

export type Staff = {
  surname: string | null;
  forename: string | null;
  email: string;
  status: StaffStatus;
};

export type GetListStaffResult = {
  staff: Staff[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
};

type UseStaffListOptions = {
  pageSize?: number;
};

export function useStaffList({ pageSize = 10 }: UseStaffListOptions) {
  const [staff, setStaff] = useState<Staff[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('all');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 1000);

    return () => clearTimeout(timer);
  }, [search]);

  const fetchData = useCallback(
    async (page: number, searchTerm: string, statusFilter: string) => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await getListStaff(page, pageSize, searchTerm, statusFilter);
        setStaff(data?.staff ?? []);
        setTotalPages(data?.pagination?.totalPages ?? 1);
        setTotalCount(data?.pagination?.totalCount ?? 0);
        setCurrentPage(page);
      } catch (err) {
        setError(err);
        setStaff([]);
        setTotalPages(1);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [pageSize],
  );

  useEffect(() => {
    fetchData(1, debouncedSearch, status);
  }, [fetchData, debouncedSearch, status]);

  const handlePageChange = useCallback(
    (page: number) => {
      const next = Math.min(Math.max(page, 1), totalPages);
      if (next !== currentPage) {
        fetchData(next, debouncedSearch, status);
      }
    },
    [currentPage, totalPages, fetchData, debouncedSearch, status],
  );

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value.substring(0, 144));
  }, []);

  const handleStatusChange = useCallback((value: string) => {
    setStatus(value);
  }, []);

  const refetch = useCallback(() => {
    fetchData(currentPage, debouncedSearch, status);
  }, [fetchData, currentPage, debouncedSearch, status]);

  return {
    staff,
    totalCount,
    currentPage,
    totalPages,
    search,
    status,
    handlePageChange,
    handleSearchChange,
    handleStatusChange,
    loading,
    error,
    refetch,
  };
}
